import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, LayoutGrid, Scale, Sparkles, TrendingUp } from "lucide-react";
import { useEffect } from "react";
import { Orb } from "@/components/Orb";
import { PLMJLogo } from "@/components/PLMJLogo";

export default function Landing() {
  const [params] = useSearchParams();
  const go = params.get("go");

  useEffect(() => {
    if (go === "build") {
      const t = setTimeout(() => {
        window.location.href = `/build`;
      }, 1600);
      return () => clearTimeout(t);
    }
  }, [go]);

  if (go === "build") {
    return <ParticipantHandoff />;
  }
  return <PresenterHub />;
}

function ParticipantHandoff() {
  return (
    <div className="relative min-h-dvh flex flex-col items-center justify-center px-6 py-10 text-center overflow-hidden">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <Orb size={200} intensity="thinking" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        className="mt-10 max-w-md"
      >
        <div className="inline-flex items-center gap-1.5 border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/60">
          <Sparkles className="h-3 w-3" /> PLMJ · Build your own Harvey
        </div>
        <h1 className="mt-4 text-4xl font-bold leading-[1.05] text-white sm:text-5xl">
          Build your{" "}
          <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-glow to-brand-400">
            AI associate
          </span>
        </h1>
        <p className="mt-4 text-base leading-relaxed text-white/70">
          In 3 minutes, design an AI associate that handles the first 80% of one recurring task in your practice.
        </p>
        <Link to="/build" className="btn-primary mt-8">
          Start building <ArrowRight className="h-4 w-4" />
        </Link>
      </motion.div>
    </div>
  );
}

function PresenterHub() {
  return (
    <div className="relative min-h-dvh px-6 py-10 overflow-hidden">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <PLMJLogo />
          <div className="text-[11px] uppercase tracking-[0.25em] text-white/40">Presenter hub</div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 text-center"
        >
          <h1 className="text-5xl font-bold leading-[1.05] text-white sm:text-6xl">
            Build your own
            <br />
            <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-glow to-brand-400">
              Harvey.
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/60">
            Live demo for the PLMJ keynote. Each lawyer designs a personal AI associate for one recurring task in their practice — in under 3 minutes, on their phone.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ExperienceCard
            tag="Live demo"
            title="Open QR for participants"
            desc="Big-screen QR + live wall: every associate built by the room, real-time. Press the card to open it on stage."
            href="/stage?tab=qr"
            Icon={Scale}
            delay={0.15}
          />
          <ExperienceCard
            tag="Closing"
            title="Show the room's results"
            desc="Aggregate hours saved per week, top practice areas, top names. The audience sees the impact they've collectively unlocked."
            href="/stage?tab=stats"
            Icon={TrendingUp}
            delay={0.23}
          />
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <Link to="/stage?tab=marketplace" className="btn-ghost justify-start">
            <LayoutGrid className="h-4 w-4" />
            Associate marketplace
          </Link>
          <Link to="/stage?tab=orb" className="btn-ghost justify-start">
            <Sparkles className="h-4 w-4" />
            Ambient presence orb
          </Link>
        </motion.div>

        <div className="mt-16 border-t border-white/5 pt-6 text-center text-[11px] uppercase tracking-widest text-white/30">
          Live demo for PLMJ · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}

function ExperienceCard({
  tag,
  title,
  desc,
  href,
  Icon,
  delay,
}: {
  tag: string;
  title: string;
  desc: string;
  href: string;
  Icon: React.ComponentType<{ className?: string }>;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
    >
      <Link
        to={href}
        className="glass glow-border group block rounded-xl p-7 transition hover:bg-white/[0.06]"
      >
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-white/50">{tag}</div>
          <Icon className="h-5 w-5 text-white/40 transition group-hover:text-cyan-glow" />
        </div>
        <div className="mt-4 text-2xl font-semibold leading-tight text-white">{title}</div>
        <p className="mt-2 text-sm text-white/60">{desc}</p>
        <div className="mt-5 flex items-center gap-1.5 text-sm text-cyan-glow">
          Open on stage
          <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
        </div>
      </Link>
    </motion.div>
  );
}
